import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type UserType = 'superadmin' | 'admin' | 'user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authenticatedSubject = new BehaviorSubject<boolean>(false);
  private readonly emailSubject = new BehaviorSubject<string>('');
  private readonly nameSubject = new BehaviorSubject<string>('');
  private readonly userTypeSubject = new BehaviorSubject<UserType>('admin');
  private readonly orgSubject = new BehaviorSubject<string>('');
  private readonly idSubject = new BehaviorSubject<string>('');

  readonly isAuthenticated$: Observable<boolean> = this.authenticatedSubject.asObservable();
  readonly email$: Observable<string> = this.emailSubject.asObservable();
  readonly name$: Observable<string> = this.nameSubject.asObservable();
  readonly userType$: Observable<UserType> = this.userTypeSubject.asObservable();

  constructor() {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType') as UserType | null;
    const email = localStorage.getItem('email');
    const name = localStorage.getItem('name');
    const org = localStorage.getItem('userOrg');
    const id = localStorage.getItem('id');

    if (token && userType) {
      this.emailSubject.next(email ?? '');
      this.nameSubject.next(name ?? '');
      this.userTypeSubject.next(userType);
      this.orgSubject.next(org ?? '');
      this.idSubject.next(id ?? '');
      this.authenticatedSubject.next(true);
    }
  }

  isAuthenticated(): boolean {
    return this.authenticatedSubject.value;
  }

  getEmail(): string {
    return this.emailSubject.value;
  }

  getName(): string {
    return this.nameSubject.value || this.deriveNameFromEmail();
  }

  setName(name: string): void {
    this.nameSubject.next(name);
  }

  getUserType(): UserType {
    return this.userTypeSubject.value;
  }

  getOrg(): string {
    return this.orgSubject.value;
  }

  getID(): string {
    return this.idSubject.value;
  }

  loginAs(type: UserType, email: string, name: string, org: string, id: string): void {
    this.emailSubject.next(email);
    this.nameSubject.next(name);
    this.userTypeSubject.next(type);
    this.orgSubject.next(org);
    this.idSubject.next(id);
    this.authenticatedSubject.next(true);

    localStorage.setItem('userType', type);
    localStorage.setItem('email', email);
    localStorage.setItem('name', name);
    localStorage.setItem('userOrg', org);
    localStorage.setItem('id', id);
  }

  logout(): void {
    this.clearCookies();
    this.authenticatedSubject.next(false);
  }

  private deriveNameFromEmail(): string {
    const local = this.emailSubject.value.split('@')[0] || 'test';
    return local.charAt(0).toUpperCase() + local.slice(1);
  }

  public clearCookies() {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('email');
    localStorage.removeItem('name');
    localStorage.removeItem('userOrg');
    localStorage.removeItem('id');
  }
}
